/**
 * `GET /api/ca-distribution` — the «Распределение по ЦА» page (owner wave 5, task T.W1).
 *
 * The shapes mirror the generated `packages/shared/src/openapi/api-types.ts`
 * (`components['schemas']['CaDistribution*']`) 1:1 — `@cb-manager/shared` does not export
 * the `src/openapi` subpath, so every api module here re-declares what it reads (the same
 * rule `./quotas` documents).
 *
 * The pivot: one row per venue that holds at least one counted application (status
 * `LINK_RECEIVED` or `EVI_PASSED`), 24 columns — the programme dictionary × the two
 * formats — keyed `"courseId:FORMAT"`, plus the venue's plan per FORMAT and the
 * best-effort `isCollege` flag the «Исключить колледжи» toggle filters on.
 */

import { useQuery } from '@tanstack/react-query';

import { requestEnvelope, type Envelope } from './client';

/** The canonical formats of a cell, OFFLINE first (the order every grid renders). */
export type CellFormat = 'OFFLINE' | 'ONLINE';

/** `"courseId:FORMAT"` — the wire key of one cell, e.g. `"31599:OFFLINE"`. */
export type CellKey = string;

/** One column: a programme × a format (the dictionary crossed, 12 × 2 = 24 today). */
export interface CaCourseColumn {
  courseId: number;
  format: CellFormat;
  /**
   * ADDITIVE (v1.8.0, A10) — the dictionary's short name (`programs.short_name`), the
   * caption the column header shows. `null` while no ADMIN has set one — the header then
   * falls back to the courseId, exactly as it did before the field existed.
   */
  shortName?: string | null;
  /**
   * ADDITIVE (v1.8.5, T.Z37) — the grade-vs-audience contradiction the venue-match sweep
   * stored for the COURSE. Both format columns of one course carry the SAME value;
   * `null` = not flagged.
   */
  audienceWarning?: AudienceWarning | null;
}

/**
 * ADDITIVE (v1.8.5, T.Z37) — the ⚠ behind one course caption: counts only, never a
 * person. The mirror of the generated `AudienceWarning` schema, which mirrors the verdict
 * the mail-worker's venue-match sweep wrote into its `sync_state MATCHER` ledger.
 */
export interface AudienceWarning {
  /** `outside / seen` — the share of grade-known applications outside the declared buckets. */
  share: number;
  /** The applications the share was taken over (≥ the worker's `AUDIENCE_GUARD_MIN_SEEN`). */
  seen: number;
  /** The declared audience, rendered («классы 10-11», «СПО / не школьный класс»). */
  expected: string;
}

/** The venue's plan per FORMAT — SUM of its quota rows (the traffic light's denominator). */
export interface CaPlans {
  OFFLINE: number;
  ONLINE: number;
}

export interface CaRow {
  venueId: number;
  /** The master dictionary name, byte-stable. */
  venueName: string;
  /**
   * BEST-EFFORT: the name carries `техникум`/`колледж`/`поу`/`профессиональн` (case-folded).
   * The flag rides on every row so the reader sees why a venue is in or out of the view.
   */
  isCollege: boolean;
  cells: Record<CellKey, number>;
  plans: CaPlans;
  /** Σ of the row's cells — the course-attributed slice. */
  total: number;
}

export interface CaTotals {
  cells: Record<CellKey, number>;
  plans: CaPlans;
  total: number;
}

export interface CaDistributionDto {
  courses: CaCourseColumn[];
  rows: CaRow[];
  totals: CaTotals;
}

export type CaDistributionEnvelope = Envelope<CaDistributionDto>;

export const CA_DISTRIBUTION_PATH = '/ca-distribution';

/** Cache identity: the pivot of one toggle state (the api answers a different pivot per value). */
export const caDistributionQueryKey = (excludeColleges: boolean) => ['ca-distribution', { excludeColleges }] as const;

/**
 * One query per toggle state. Refetch on mount after 30s like the roster's #6: the page is
 * a working screen the manager re-checks as applications arrive.
 */
export function useCaDistribution(excludeColleges: boolean) {
  return useQuery({
    queryKey: caDistributionQueryKey(excludeColleges),
    queryFn: async ({ signal }): Promise<CaDistributionDto> =>
      (
        await requestEnvelope<CaDistributionDto>(
          'GET',
          `${CA_DISTRIBUTION_PATH}?excludeColleges=${excludeColleges}`,
          ['data'],
          { signal },
        )
      ).data,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}
